import { AgentBookingModel } from '@/__generated';
import BookingHeader from '@/components/App/Header/BookingHeader';
import HotelJapaneseName from '@/components/App/Hotels/HotelJapaneseName';
import PageLoading from '@/components/App/PageLoading';
import SingleOrPluralText from '@/components/App/SingleOrPluralText';
import { appDisplayDateFormat } from '@/config';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { getFullName } from '@/helpers/string';
import { formatPriceWithCurrency, useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { apiSdk } from '@/httpClient';
import { AppUrls } from '@/urls';
import moment from 'moment';
import React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { BookingSource, BookingStatus, HotelRoomStatus, PaymentStatus } from '@/constants';
import NtaTag from '@/components/NtaTag';
import BooleanTag from '@/components/App/BooleanTag';
import { Helmet } from 'react-helmet';
import DefaultCancellationPolicy from '../HotelDetail/components/DefaultCancellationPolicy';
import LabelValueItem from '@/components/App/BookingComponents/LabelValueItem';
import PlanTaxAndFee from '../HotelDetail/components/PlanTaxAndFee';
import SamePaxAndNextBookingButton from '@/components/App/BookingComponents/SamePaxAndNextBookingButton';
import { handleBookingNotFound } from '@/helpers/errorHandle';
import { orderBy, sumBy } from 'lodash';
import AgentBookingRef from './components/AgentBookingRef';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCancel, faCheck, faCircleInfo, faDownload } from '@fortawesome/free-solid-svg-icons';
import PartialBookedConfirmModal from '@/components/App/BookingComponents/PartialBookedConfirmModal';
import CancelBookingModal from '@/components/App/BookingComponents/CancelBookingModal';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { exportService } from '@/services/exportService';
import bookingService from '@/services/bookingService';
import { useTranslation } from 'react-i18next';
import RoomPlanMealDetail from '@/components/App/RoomPlanMealDetail';
import { UncontrolledTooltip } from 'reactstrap';
import CancelFaBookingModal from '@/components/App/BookingComponents/CancelFaBookingModal';
import PriceOrTBA from '@/components/App/BookingComponents/PriceOrTBA';

type Props = {};
const fieldClass = 'col-md-3 col-xs-12';
export default function BookingDetailPage({}: Props) {
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState<boolean>(true);
  const history = useHistory();
  const { bookingId } = useParams<{ bookingId: string }>();
  const { formatCurrency } = useCurrencyFormat();
  const [booking, setBooking] = React.useState<AgentBookingModel>();
  const [displayPartialBookedModal, setDisplayPartialBookedModal] = React.useState<number>(0); //1 default Proceed, 2 default cancel
  const [displayCancelBookingModal, setDisplayCancelBookingModal] = React.useState(false);
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }
  React.useEffect(() => {
    if (bookingId) {
      getBookingDetail();
    } else {
      history.push(AppUrls.HomePage);
    }
  }, [bookingId]);

  const getBookingDetail = () => {
    setLoading(true);
    apiSdk.BookingService.getBookingDetails({ id: bookingId })
      .then(res => {
        if (res) {
          setBooking(res);
        } else {
          handleBookingNotFound();
        }
      })
      .catch(() => {
        handleBookingNotFound();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const cancellationDate = React.useMemo(() => {
    let roomCancelled = booking?.hotel?.rooms?.filter(x => x.cancelledAt).map(x => x.cancelledAt);
    if (roomCancelled?.length) {
      return orderBy(roomCancelled, x => moment(x))[0];
    }
    return null;
  }, [booking]);

  const onBookingRefChange = (newRef: any) => {
    setBooking(b => ({ ...b, agentBookingRef: newRef }));
  };

  const isPartialBooking = React.useMemo(() => {
    return [BookingStatus.PartialBooked, BookingStatus.PartialHold].includes(booking?.bookingStatus as any);
  }, [booking]);

  const displayCancelBooking = React.useMemo(() => {
    if (isPartialBooking) {
      return false;
    }
    return ![BookingStatus.Failed, BookingStatus.Cancelled].includes(booking?.bookingStatus as any);
  }, [booking, isPartialBooking]);

  const canAddSameGuest = React.useMemo(() => {
    return [BookingSource.Omega].includes(booking?.source as any) && booking?.refNumber && booking?.roomSearchInfos?.length;
  }, [booking]);

  const cancelBooking = () => {
    bookingService
      .handleOnCancelBooking(
        booking?.hotel?.rooms?.map(r => ({
          checkInDate: booking?.checkinDate,
          supplier: r.supplier as any,
          status: r.status as any,
        })) || [],
      )
      .then(next => {
        if (next) {
          setDisplayCancelBookingModal(true);
        }
      });
  };

  const downloadBookingPdf = (bookingId: string) => {
    exportService.exportBookingPdf(bookingId);
  };

  if (loading) {
    return <PageLoading />;
  }

  if (!booking) {
    return null;
  }

  let { bookingContactDetail, bookingLeadPaxDetail, hotel } = booking;

  return (
    <div className="mb-20">
      <Helmet>
        <title>{`Booking detail - ${booking.refNumber}`}</title>
      </Helmet>
      {displayPartialBookedModal ? (
        <PartialBookedConfirmModal
          defaultConfirm={displayPartialBookedModal === 1}
          bookingId={bookingId}
          onClose={() => setDisplayPartialBookedModal(0)}
        />
      ) : null}
      {displayCancelBookingModal ? (
        <>
          {booking.source === BookingSource.ForAgent ? (
            <CancelFaBookingModal
              bookingId={bookingId}
              onCloseModal={reload => {
                if (reload) {
                  getBookingDetail();
                }
                setDisplayCancelBookingModal(false);
              }}
            />
          ) : (
            <CancelBookingModal
              bookingId={bookingId}
              onCloseModal={reload => {
                if (reload) {
                  getBookingDetail();
                }
                setDisplayCancelBookingModal(false);
              }}
            />
          )}
        </>
      ) : null}

      <BookingHeader isSticky />
      <div className="header-margin" />
      <Helmet>
        <title>{`${booking.refNumber} - ${booking.orderId}`}</title>
      </Helmet>
      <div className="container pt-50">
        <div className="d-flex x-gap-10 justify-content-end mb-20">
          {displayCancelBooking ? (
            <button className={'button px-20 py-15 -dark-1 bg-red-1 text-dark-1 mr-5 text-white'} onClick={() => cancelBooking()}>
              <FontAwesomeIcon icon={faCancel} className="mr-5" />
              Cancel
            </button>
          ) : null}
          {bookingService.canConfirmOnHoldBooking(booking) ? (
            <Link
              to={AppUrls.__createOnHoldPaymentUrl(booking.id || '')}
              className="button px-20 py-15 -dark-1 text-dark-1 mr-5 text-white bg-blue-1"
            >
              <FontAwesomeIcon icon={faCheck} className="text-12 mr-5" />
              {t('base.Confirm')}
            </Link>
          ) : null}
          {isPartialBooking ? (
            <>
              <button className="button px-20 py-15 -dark-1 bg-blue-1 text-white mr-10" onClick={() => setDisplayPartialBookedModal(1)}>
                Decide <FontAwesomeIcon icon={faCheck} className="ml-15" />
              </button>
              <button className="button px-20 py-15 -dark-1 bg-red-1 text-dark-1 mr-5 text-white" onClick={() => setDisplayPartialBookedModal(2)}>
                Cancel <FontAwesomeIcon icon={faCancel} className="ml-15" />
              </button>
            </>
          ) : null}
          {booking.bookingStatus === BookingStatus.Booked ? (
            <button className="button px-20 py-15 -dark-1 bg-blue-1 text-dark-1 mr-5 text-white" onClick={() => downloadBookingPdf(booking.id || '')}>
              Download Hotel Voucher <FontAwesomeIcon icon={faDownload} className="ml-15" />
            </button>
          ) : null}

          {canAddSameGuest ? (
            <SamePaxAndNextBookingButton bookingRefNumber={booking?.refNumber || ''} rooms={booking?.roomSearchInfos || []} />
          ) : null}
        </div>
        <div className="row x-gap-10 y-gap-10">
          <div className="col-12 pb-10">
            <div className="d-flex align-items-center">
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
              <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase px-10">Booking summary</div>
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
            </div>
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Booking Number" value={booking.refNumber} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Order ID" value={booking.orderId} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Agent Name" value={booking.agentName} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Agent User ID" value={booking.agentUserUserName} />
          </div>

          <div className={fieldClass}>
            <LabelValueItem label="Booking Date" value={appDisplayDateFormat.default.withTime(booking.createdAt)} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Cancelling Date" value={appDisplayDateFormat.default.withTime(cancellationDate)} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Check-in Date" value={appDisplayDateFormat.default.dateOnly(booking.checkinDate)} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Check-out Date" value={appDisplayDateFormat.default.dateOnly(booking.checkoutDate)} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Guests" value={`${booking.noOfAdult} adult(s) ${booking.noOfChild} child`} />
          </div>
          <div className="col-md-6 col-xs-12">
            <LabelValueItem label="Payment Method" value={booking.paymentMethod} />
          </div>
          <div className="col-md-3 col-xs-12">
            <AgentBookingRef bookingId={booking.id || ''} agentBookingRef={booking.agentBookingRef} onBookingRefChange={onBookingRefChange} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem
              label="Booking Status"
              value={
                <NtaTag size={16} text={BookingStatus.__getValue(booking.bookingStatus)} color={BookingStatus.__getColor(booking.bookingStatus)} />
              }
            />
          </div>

          <div className={fieldClass}>
            <LabelValueItem
              label="Payment Status"
              value={
                <NtaTag size={16} text={PaymentStatus.__getValue(booking.paymentStatus)} color={PaymentStatus.__getColor(booking.paymentStatus)} />
              }
            />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Currency" value={booking.currency} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem
              label="Total Price"
              value={
                <PriceOrTBA
                  cancelledAt={booking.hotel?.rooms?.find(x => x.cancelledAt)?.cancelledAt}
                  currency={booking.currency}
                  faCancelChargeDate={booking.faCancelChargeDate}
                  price={booking.sellingPrice}
                  source={booking.source}
                />
              }
            />
          </div>

          {/*  Contact detail */}
          <div className="col-12  pt-20 pb-10">
            <div className="d-flex align-items-center">
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
              <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase px-10">PERSON IN CHARGE</div>
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
            </div>
          </div>
          <div className="col-md-6 col-xs-12">
            <LabelValueItem label="Full Name" value={getFullName(bookingContactDetail?.firstName, bookingContactDetail?.lastName)} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Email" value={bookingContactDetail?.email} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem
              label="Phone Number"
              value={[bookingContactDetail?.phoneCode, bookingContactDetail?.phoneNumber].filter(x => x).join('-')}
            />
          </div>

          {/*  Lead passenger detail */}
          <div className="col-12  pt-20 pb-10">
            <div className="d-flex align-items-center">
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
              <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase px-10">Lead passenger detail</div>
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
            </div>
          </div>

          <div className="col-md-6 col-xs-12">
            <LabelValueItem label="Full Name" value={getFullName(bookingLeadPaxDetail?.firstName, bookingLeadPaxDetail?.lastName)} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Email" value={bookingLeadPaxDetail?.email} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem
              label="Phone Number"
              value={[bookingLeadPaxDetail?.phoneCode, bookingLeadPaxDetail?.phoneNumber].filter(x => x).join('-')}
            />
          </div>
          {/*  */}
          <div className="col-12  pt-20 pb-10">
            <div className="d-flex align-items-center">
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
              <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase px-10">Hotel detail</div>
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
            </div>
          </div>

          <div className="col-md-9 col-xs-12">
            <LabelValueItem
              label="Hotel Name"
              value={
                <>
                  {hotel?.hotelName}
                  <HotelJapaneseName name={hotel?.hotelNameJp} addBefore="-" />
                </>
              }
            />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Hotel Code" value={hotel?.hotelCode} />
          </div>
          <div className="col-md-9 col-xs-12">
            <LabelValueItem label="Address" value={hotel?.address} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Postal Code" value={hotel?.postalCode} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Check-in Time" value={hotel?.checkinTime} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Check-out Time" value={hotel?.checkoutTime} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Tel" value={hotel?.tel} />
          </div>
          <div className={fieldClass}>
            <LabelValueItem label="Fax" value={hotel?.fax} />
          </div>
          <div className="col-12  pt-20 pb-10">
            <div className="d-flex align-items-center">
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
              <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase px-10">Room detail</div>
              <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
            </div>
          </div>
          <div className="col-12">
            <div className="p-2">
              {hotel?.rooms?.map((room, index) => {
                let roomStatus = HotelRoomStatus.__getValue(room.status);
                if ([BookingStatus.Hold, BookingStatus.PartialHold].includes(booking.bookingStatus as any)) {
                  if (room.status === HotelRoomStatus.Booked) {
                    roomStatus = 'Hold';
                  }
                }

                return (
                  <div className="row x-gap-10 y-gap-10 border-light rounded mb-20" key={index}>
                    <div className="col-12 fw-500 text-18 px-10 py-5 bg-green-2 text-white mb-10">{`Room #${index + 1}`}</div>
                    <div className="row x-gap-10 y-gap-10 p-2">
                      <div className={fieldClass}>
                        <LabelValueItem
                          label="Room Status"
                          value={<NtaTag size={16} text={roomStatus} color={HotelRoomStatus.__getColor(room.status)} />}
                        />
                      </div>
                      <div className={fieldClass}>
                        <LabelValueItem label="Cancelled Date (JST)" value={appDisplayDateFormat.default.withTime(room.cancelledAt)} />
                      </div>

                      <div className={fieldClass}>
                        <LabelValueItem label="Guests" value={`${room.noOfAdult} adult(s) ${room.noOfChild} child`} />
                      </div>
                      <div className={fieldClass}>
                        <LabelValueItem
                          label="No. of Rooms"
                          value={<SingleOrPluralText count={room.noOfRooms || 0} singleText="{} room" pluralText="{} rooms" />}
                        />
                      </div>
                      <div className={fieldClass}>
                        <LabelValueItem label="Room Category" value={room?.roomTypeCategory} />
                      </div>

                      <div className={fieldClass}>
                        <LabelValueItem label="Room Type Name" value={room?.roomTypeName} />
                      </div>

                      <div className="col-md-6 col-xs-12">
                        <LabelValueItem
                          label="Meal Type"
                          value={
                            <div className="d-flex">
                              {[room.boardTypeCode, room?.boardTypeName].filter(x => x).join(' ')}
                              <RoomPlanMealDetail planDetail={room} supplier={room.supplier} />
                            </div>
                          }
                        />
                      </div>

                      <div className="col-md-6 col-xs-12">
                        <LabelValueItem label="Plan" value={[room.planCode, room.planName].filter(x => x).join(' ')} />
                      </div>
                      {room.planTitle ? (
                        <div className="col-md-6 col-xs-12">
                          <LabelValueItem
                            label="Plan Detail"
                            value={
                              <div className="d-flex">
                                {room.planTitle}
                                {room.planDesc ? (
                                  <div>
                                    <FontAwesomeIcon
                                      id={`room-plan-title-${index}`}
                                      icon={faCircleInfo}
                                      className="text-blue-1 ml-5"
                                      style={{ cursor: 'pointer' }}
                                    />

                                    <UncontrolledTooltip popperClassName="w-500px" target={`#room-plan-title-${index}`} trigger="focus">
                                      <div className="text-14">{room.planDesc}</div>
                                    </UncontrolledTooltip>
                                  </div>
                                ) : null}
                              </div>
                            }
                          />
                        </div>
                      ) : null}
                      <div className={fieldClass}>
                        <LabelValueItem label="Check-in Time" value={room.checkinTime || hotel?.checkinTime} />
                      </div>
                      <div className={fieldClass}>
                        <LabelValueItem label="Check-out Time" value={hotel?.checkoutTime} />
                      </div>
                      <div className={fieldClass}>
                        <LabelValueItem label="Total Price" value={formatPriceWithCurrency(room.price, booking.currency)} />
                      </div>

                      <div className={fieldClass}>
                        <LabelValueItem label="Taxes & Fees" value={<PlanTaxAndFee plan={room} />} />
                      </div>
                      <div className={fieldClass}>
                        <LabelValueItem
                          label="Cancellation Fee"
                          value={
                            <PriceOrTBA
                              cancelledAt={room.cancelledAt}
                              currency={booking.currency}
                              faCancelChargeDate={booking.faCancelChargeDate}
                              price={room.cancellationFee}
                              source={booking.source}
                            />
                          }
                        />
                      </div>
                      <div className={fieldClass}>
                        <LabelValueItem label="Supplier Reference" value={room.supplierRefNumber} />
                      </div>
                      <div className="col-md-12 col-xs-12">
                        <LabelValueItem
                          label="Cancellation Policy"
                          value={<DefaultCancellationPolicy fontSize={16} checkinDate={booking.checkinDate} plan={room} />}
                        />
                      </div>
                      {room.supplier === 'NTA' ? (
                        <div className="col-md-12 col-xs-12">
                          <LabelValueItem
                            label="Payment Notes"
                            value={
                              room.remarks ? (
                                <>
                                  <div className="text-red-1">Infant Remarks: {room.remarks}</div>
                                  {room.paymentNotes}
                                </>
                              ) : (
                                room.paymentNotes
                              )
                            }
                          />
                        </div>
                      ) : (
                        <div className="col-12">
                          <LabelValueItem
                            label="Payment Notes"
                            value={
                              <>
                                <div className="text-red-1">{room.remarks}</div>
                                {room.paymentNotes}
                              </>
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
